import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';

export type SceneId = 'Game' | 'MainMenu' | 'Editor' | 'PVP';

export interface Scene {
  id: SceneId;
  view: Container;
  destroy(): void;
  onActivated?(): void;
  update?(deltaTime: number): void;
  resize?(width: number, height: number): void;
}

export class SceneManager {
  public readonly container: Container = new Container();

  private width: number;

  private height: number;

  #activeScene: Scene | null = null;

  public readonly onSceneActivated = new Signal<(scene: Scene) => void>();

  public readonly onSceneDestroyed = new Signal<(scene: Scene) => void>();

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  public activateScene(scene: Scene): void {
    this.killActiveScene();
    this.container.addChild(scene.view);
    scene.resize?.(this.width, this.height);
    this.#activeScene = scene;
    this.onSceneActivated.emit(scene);
    scene.onActivated?.();
  }

  public killActiveScene(): void {
    if (!this.#activeScene) return;
    this.container.removeChild(this.#activeScene.view);
    this.#activeScene.destroy();
    this.onSceneDestroyed.emit(this.#activeScene);
    this.#activeScene = null;
  }

  public update(deltaTime: number): void {
    if (!this.#activeScene) return;
    this.#activeScene.update?.(deltaTime);
  }

  public resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    if (!this.#activeScene) return;
    this.#activeScene.resize?.(this.width, this.height);
  }

  get activeScene(): Scene | null {
    return this.#activeScene;
  }
}
